import React from 'react';
import { StyleSheet, TextInput as RNTextInput, View } from 'react-native';
import { Picker } from '@react-native-picker/picker';

import { NumericField, Text } from '~/components/core';
import { StrategyNodeAttributeTemplate } from '~/api-services/AIStrategyBuilderService/types';
import { useStyles } from '~/hooks';
import { useTheme } from '~/hooks/useTheme';
import { FONT_SIZE_S, FONT_SIZE_XS } from '~/constants/typography';
import { scaleSize } from '~/constants/scale';
import { Theme } from '~/theme';

type FormProps = {
    attrs: StrategyNodeAttributeTemplate[];
    value: Record<string, unknown>;
    onChange: (value: Record<string, unknown>) => void;
};

// Editable indicator-parameter form driven entirely by the backend node templates: bounded
// numerics get a NumericField, enumerated fields a picker, everything else a text input;
// nested groups (e.g. period.min/max search ranges) recurse. Ported from the PoC wizard.
export default function NodeAttributeForm({ attrs, value, onChange }: FormProps) {
    return (
        <>
            {attrs
                .filter((attr) => !attr.hidden)
                .map((attr) => (
                    <NodeAttributeInput
                        key={attr.name}
                        attr={attr}
                        value={value[attr.name]}
                        onChange={(v) => onChange({ ...value, [attr.name]: v })}
                    />
                ))}
        </>
    );
}

type InputProps = {
    attr: StrategyNodeAttributeTemplate;
    value: unknown;
    onChange: (value: unknown) => void;
};

// Constraint types that declare one field must stay on one side of another — the backend's own
// signal that two fields are the ends of a range rather than unrelated settings.
const ORDERING_CONSTRAINTS = ['lessThan', 'lessThanOrEqual', 'greaterThan', 'greaterThanOrEqual'];

const isBoundedNumeric = (attr: StrategyNodeAttributeTemplate): boolean =>
    (attr.valueType === 'int' || attr.valueType === 'float') &&
    !attr.readonly &&
    !attr.hidden &&
    attr.minValue !== undefined &&
    attr.maxValue !== undefined;

/**
 * A group that is really one search range (e.g. period.min / period.max): exactly two visible
 * bounded numeric children the backend has declared ordered w.r.t. each other. Those two get
 * laid out side by side so they read as a single range; every other group — a third editable
 * field, unbounded fields, no ordering constraint — falls through to the stacked layout
 * unchanged. Hidden fields are discounted first: `period` also carries a hidden readonly
 * `type`, which the form never renders either way.
 *
 * The API's own field order is preserved rather than derived from the constraint direction, so
 * the columns always match the order the template declares.
 */
const rangePair = (attr: StrategyNodeAttributeTemplate): StrategyNodeAttributeTemplate[] | null => {
    const fields = (attr.fields ?? []).filter((f) => !f.hidden);
    if (fields.length !== 2 || !fields.every(isBoundedNumeric)) {
        return null;
    }
    const names = fields.map((f) => f.name);
    const ordered = (attr.constraints ?? []).some(
        (c) => ORDERING_CONSTRAINTS.includes(c.type) && names.includes(c.left) && names.includes(c.right)
    );
    return ordered ? fields : null;
};

const NodeAttributeInput = ({ attr, value, onChange }: InputProps) => {
    const styles = useStyles(stylesGenerator);
    const theme = useTheme();

    if (attr.fields && attr.fields.length > 0) {
        const nested = (value && typeof value === 'object' ? (value as Record<string, unknown>) : {}) ?? {};
        const pair = rangePair(attr);
        return (
            <View style={styles.group}>
                <Text noTranslate style={styles.groupLabel}>
                    {attr.name}
                </Text>
                {pair ? (
                    <View style={styles.rangeRow}>
                        {pair.map((field) => (
                            <View key={field.name} style={styles.rangeColumn}>
                                <NodeAttributeInput
                                    attr={field}
                                    value={nested[field.name]}
                                    onChange={(v) => onChange({ ...nested, [field.name]: v })}
                                />
                            </View>
                        ))}
                    </View>
                ) : (
                    <NodeAttributeForm attrs={attr.fields} value={nested} onChange={onChange} />
                )}
            </View>
        );
    }

    const isNumeric = attr.valueType === 'int' || attr.valueType === 'float';
    const text = value === undefined || value === null ? '' : String(value);

    const handleChange = (next: string) => {
        if (!isNumeric) {
            onChange(next);
            return;
        }
        if (next === '' || next === '-' || next === '.') {
            onChange(next);
            return;
        }
        const parsed = attr.valueType === 'int' ? parseInt(next, 10) : parseFloat(next);
        onChange(Number.isFinite(parsed) ? parsed : next);
    };

    // Bounded numeric fields (e.g. period.min / period.max) get the stepper field.
    if (isNumeric && !attr.readonly && attr.minValue !== undefined && attr.maxValue !== undefined) {
        const isInt = attr.valueType === 'int';
        const numValue = typeof value === 'number' ? value : Number(value);
        const safeValue = Number.isFinite(numValue) ? numValue : attr.minValue;
        return (
            <NumericField
                label={attr.name}
                value={safeValue}
                onChange={(v) => onChange(v)}
                min={attr.minValue}
                max={attr.maxValue}
                step={isInt ? 1 : 0.01}
                precision={isInt ? 0 : 2}
                hint={`${attr.minValue} – ${attr.maxValue}`}
            />
        );
    }

    if (attr.options && attr.optionValues && attr.optionValues.length > 0) {
        return (
            <View style={styles.field}>
                <Text noTranslate style={styles.label}>
                    {attr.name}
                </Text>
                <View style={styles.pickerWrap}>
                    <Picker
                        enabled={!attr.readonly}
                        selectedValue={text}
                        onValueChange={(v) => onChange(v)}
                        style={styles.picker}
                        dropdownIconColor={theme.text}
                    >
                        <Picker.Item label="—" value="" />
                        {attr.optionValues.map((opt) => {
                            const label = opt === undefined || opt === null ? '' : String(opt);
                            return <Picker.Item key={label} label={label} value={label} />;
                        })}
                    </Picker>
                </View>
            </View>
        );
    }

    return (
        <View style={styles.field}>
            <Text noTranslate style={styles.label}>
                {attr.readonly ? `${attr.name} (readonly)` : attr.name}
            </Text>
            <RNTextInput
                style={[styles.input, attr.readonly ? styles.inputDisabled : null]}
                value={text}
                editable={!attr.readonly}
                onChangeText={handleChange}
                keyboardType={isNumeric ? 'numeric' : 'default'}
                placeholder={attr.valueType}
                placeholderTextColor={theme.textSecondary}
            />
        </View>
    );
};

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        group: {
            marginBottom: scaleSize(6),
        },
        groupLabel: {
            color: theme.textSecondary,
            fontSize: FONT_SIZE_XS,
            marginBottom: scaleSize(4),
            textTransform: 'capitalize',
        },
        rangeRow: {
            flexDirection: 'row',
            gap: scaleSize(10),
        },
        rangeColumn: {
            flex: 1,
        },
        field: {
            marginBottom: scaleSize(10),
        },
        label: {
            color: theme.text,
            fontSize: FONT_SIZE_S,
            marginBottom: scaleSize(4),
        },
        pickerWrap: {
            borderWidth: 1,
            borderColor: theme.equityValue,
            borderRadius: scaleSize(8),
            backgroundColor: theme.backgroundSecondary,
            overflow: 'hidden',
        },
        picker: {
            color: theme.text,
            backgroundColor: 'transparent',
            borderWidth: 0,
            height: scaleSize(38),
            paddingHorizontal: scaleSize(8),
        },
        input: {
            borderWidth: 1,
            borderColor: theme.equityValue,
            borderRadius: scaleSize(8),
            backgroundColor: theme.backgroundSecondary,
            color: theme.text,
            fontSize: FONT_SIZE_S,
            paddingHorizontal: scaleSize(10),
            paddingVertical: scaleSize(8),
        },
        inputDisabled: {
            opacity: 0.5,
        },
    });
